import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, Trash2, MessageSquare, Send, Pencil, Check, X, ImagePlus, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { BuildComment, BuildProjectMedia } from '../../types/buildTeam';
import { BuildProjectMediaThumbnail } from './BuildProjectMediaThumbnail';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import { CreatorProfile } from '../CreatorProfile';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export type BuildCommentsTargetType = 'project' | 'build';

export interface BuildCommentsSectionProps {
  targetId: string;
  targetType: BuildCommentsTargetType;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

async function fetchComments(
  targetType: BuildCommentsTargetType,
  targetId: string
): Promise<BuildComment[]> {
  if (targetType === 'project') {
    return buildTeamApiService.getProjectComments(targetId);
  }
  return buildTeamApiService.getBuildComments(targetId);
}

async function addComment(
  targetType: BuildCommentsTargetType,
  targetId: string,
  content: string,
  imageFiles: File[]
): Promise<BuildComment> {
  if (imageFiles.length > 0) {
    const formData = new FormData();
    formData.append('content', content);
    imageFiles.forEach((f, i) => {
      formData.append(`files[${i}]`, f);
    });
    if (targetType === 'project') {
      return buildTeamApiService.addProjectCommentWithImages(targetId, formData);
    }
    return buildTeamApiService.addBuildCommentWithImages(targetId, formData);
  }
  if (targetType === 'project') {
    return buildTeamApiService.addProjectComment(targetId, content);
  }
  return buildTeamApiService.addBuildComment(targetId, content);
}

function commentImages(comment: BuildComment): BuildProjectMedia[] {
  return comment.images ?? [];
}

export function BuildCommentsSection({
  targetId,
  targetType,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: BuildCommentsSectionProps) {
  const [comments, setComments] = useState<BuildComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newContent, setNewContent] = useState('');
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'delete'; id: string } | null>(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchComments(targetType, targetId);
      const sorted = [...data].sort((a, b) => a.created_at - b.created_at);
      setComments(sorted);
      const authorIds = sorted.map((c) => c.author_id).filter(Boolean);
      usernameCacheService.prefetchUsernames(authorIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = newContent.trim();
    if (!content || !canCreate || submitting) return;
    setSubmitting(true);
    try {
      await addComment(targetType, targetId, content, pendingImages);
      setNewContent('');
      setPendingImages([]);
      await loadComments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (comment: BuildComment) => {
    setEditingId(comment.id);
    setEditDraft(comment.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const handleSaveEdit = async (commentId: string) => {
    const content = editDraft.trim();
    if (!content || !canEdit || savingEditId) return;
    setSavingEditId(commentId);
    try {
      await buildTeamApiService.updateComment(commentId, content);
      setEditingId(null);
      setEditDraft('');
      await loadComments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!canDelete || deletingId) return;
    setConfirmAction({ type: 'delete', id: commentId });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    setDeletingId(confirmAction.id);
    try {
      await buildTeamApiService.deleteComment(confirmAction.id);
      await loadComments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeletingId(null);
      setConfirmAction(null);
    }
  };

  const formatDate = (ts: number) => {
    const date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonLoader className="h-4 w-28" />
        <div className="divide-y divide-glass-200/10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3 first:pt-0 space-y-2">
              <div className="flex items-center gap-2">
                <SkeletonLoader className="h-6 w-6 rounded-md" />
                <SkeletonLoader className="h-3 w-24" />
                <SkeletonLoader className="h-3 w-16" />
              </div>
              <SkeletonLoader className="h-4 w-4/5" />
              <SkeletonLoader className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load comments"
        description={error}
        action={
          <button type="button" onClick={loadComments} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Comments" icon={MessageSquare} count={comments.length} className="mb-0" />

      <div>
        {comments.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={MessageSquare}
            title="No comments yet"
            description={canCreate ? 'Start the discussion below.' : 'Comments on this record will appear here.'}
          />
        ) : (
          <div className="divide-y divide-glass-200/10">
          <AnimatePresence>
          {comments.map((comment) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CreatorProfile creatorId={comment.author_id} inline />
                  <span className="text-xs text-glass-500 tabular-nums">{formatDate(comment.created_at)}</span>
                </div>
                {editingId === comment.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      className="glass-input w-full resize-none"
                      disabled={savingEditId === comment.id}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(comment.id)}
                        disabled={!editDraft.trim() || savingEditId === comment.id}
                        className="btn-primary"
                      >
                        {savingEditId === comment.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={savingEditId === comment.id}
                        className="glass-button-secondary"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-white whitespace-pre-wrap">{comment.content}</p>
                    {commentImages(comment).length > 0 && (
                      <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {commentImages(comment).map((img) => (
                          <li key={img.id}>
                            <BuildProjectMediaThumbnail
                              media={img}
                              className="aspect-video w-full"
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
              {(canEdit || canDelete) && editingId !== comment.id && (
                <div className="flex flex-shrink-0 gap-0.5">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => startEdit(comment)}
                      className="btn-icon touch-manipulation"
                      title="Edit comment"
                      aria-label="Edit comment"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(comment.id)}
                      disabled={deletingId === comment.id}
                      className="btn-icon text-red-400 hover:text-red-300 touch-manipulation"
                      title="Delete comment"
                      aria-label="Delete comment"
                    >
                      {deletingId === comment.id ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          ))}
          </AnimatePresence>
          </div>
        )}
      </div>

      {canCreate && (
        <form onSubmit={handleSubmit} className="space-y-2 pt-4 border-t border-glass-200/10">
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Write a comment…"
            rows={2}
            className="glass-input w-full resize-none"
            disabled={submitting}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <label className="glass-button-sm cursor-pointer touch-manipulation">
                <ImagePlus className="w-4 h-4" />
                Attach images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  disabled={submitting}
                  onChange={(e) => {
                    const list = e.target.files;
                    if (!list?.length) return;
                    setPendingImages((prev) => [...prev, ...Array.from(list)]);
                    e.target.value = '';
                  }}
                />
              </label>
              {pendingImages.length > 0 && (
                <span className="text-xs text-glass-400 tabular-nums">
                  {pendingImages.length} image{pendingImages.length !== 1 ? 's' : ''} selected
                </span>
              )}
              {pendingImages.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPendingImages([])}
                  className="text-xs text-glass-500 hover:text-white underline underline-offset-2"
                  disabled={submitting}
                >
                  Clear images
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!newContent.trim() || submitting}
              className="btn-primary"
            >
              {submitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
          </div>
        </form>
      )}

      {confirmAction && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmAction(null)}
          onConfirm={executeConfirmAction}
          title="Delete Comment"
          message="Are you sure you want to delete this comment?"
          confirmText="Delete"
          isLoading={!!deletingId}
        />
      )}
    </div>
  );
}
